import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, LogIn, Save, RefreshCw, Users, DollarSign, Sliders, Globe2, ShieldAlert,
  Tag, HeartHandshake, LineChart, Flame, Compass, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import * as api from "@/lib/api";

const TOKEN_KEY = "atlas_admin_token";
// Admin token is stored in sessionStorage (not localStorage) so it is scoped to
// the current tab and cleared automatically when the tab/window is closed. This
// reduces XSS blast radius: a persisted token in localStorage would remain
// available across sessions to any script running on the origin. On first load
// we also migrate + purge any legacy localStorage copy left behind by earlier
// versions of the admin page.
try {
  const legacy = typeof window !== "undefined" && window.localStorage
    ? window.localStorage.getItem(TOKEN_KEY)
    : null;
  if (legacy) {
    // Copy across only if the session doesn't already hold a value, so we don't
    // overwrite a fresher token entered in a new tab.
    if (window.sessionStorage && !window.sessionStorage.getItem(TOKEN_KEY)) {
      window.sessionStorage.setItem(TOKEN_KEY, legacy);
    }
    window.localStorage.removeItem(TOKEN_KEY);
  }
} catch {
  // Storage may be disabled (private mode, quota errors, etc.) — the admin
  // panel still works, the user will just need to re-enter their token.
}
const WEIGHT_KEYS = ["timeline", "cost", "physical_presence", "difficulty"];
const curSym = (c) => (c === "eur" ? "€" : c === "usd" ? "$" : "");

const DEFAULT_PRICING_SHAPE = {
  currency: "eur",
  citizenship: { label: "Citizenship Report", blurb: "", standard: 40, promo_price: 19.99 },
  full: { label: "Full Mobility Report", blurb: "", standard: 89, promo_price: 44.5 },
  upgrade: { label: "Upgrade to Full Mobility Report", standard: 49, promo_price: 24.5 },
  promo: { active: true, label: "50% off launch" },
};

/**
 * LifestyleSeedCard — one-click post-deploy utility.
 *
 * The lifestyle + mobility blocks for the ~50 covered countries are
 * curated in /app/backend/data/lifestyle_seed.py. On a fresh Mongo (e.g.
 * right after a production deploy where the countries collection was
 * seeded before the lifestyle-layer feature existed) those blocks are
 * missing and the /match shortlist returns zero rows.
 *
 * This button hits POST /api/admin/lifestyle/seed which upserts the blocks
 * onto each seeded country. Idempotent — clicking twice reports 0 updates
 * on the second run. Result summary shown inline so the operator has
 * immediate visual confirmation.
 */
const LifestyleSeedCard = ({ token }) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await api.adminLifestyleSeed(token);
      setResult(r);
      if ((r.updated ?? 0) > 0) {
        toast.success(`Seeded ${r.updated} countries.`);
      } else if ((r.missing_from_dataset || []).length === 0) {
        toast.success("Lifestyle data already current.");
      } else {
        toast.warning(`${r.missing_from_dataset.length} countries not found in dataset.`);
      }
    } catch {
      toast.error("Seed failed. Check the backend logs.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="p-5 bg-white" data-testid="admin-lifestyle-seed-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Compass className="h-4 w-4 text-[hsl(var(--atlas-teal))]" />
            <h3 className="font-display font-semibold text-[hsl(var(--atlas-navy))]">
              Lifestyle + mobility seed
            </h3>
          </div>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Writes the curated lifestyle (climate, coast, cost of living,
            healthcare, safety, English) and mobility (Schengen / EU / Mercosur
            zones + Henley passport strength) blocks onto the ~50 covered
            countries. Idempotent — safe to click after every deploy.
          </p>
        </div>
        <Button
          onClick={run}
          disabled={busy}
          className="bg-[hsl(var(--atlas-navy))] hover:bg-[hsl(var(--atlas-navy-2))] text-white"
          data-testid="admin-lifestyle-seed-button"
        >
          {busy ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Seeding…</>
          ) : (
            <><Sparkles className="h-4 w-4 mr-2" /> Run seed</>
          )}
        </Button>
      </div>
      {result && (
        <div
          className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm"
          data-testid="admin-lifestyle-seed-result"
        >
          <div className="rounded-lg border p-2">
            <div className="text-xs text-muted-foreground">Total seeded</div>
            <div className="font-display text-xl font-semibold text-[hsl(var(--atlas-navy))] tabular-nums">
              {result.seeded_countries}
            </div>
          </div>
          <div className="rounded-lg border p-2">
            <div className="text-xs text-muted-foreground">Updated now</div>
            <div className="font-display text-xl font-semibold text-[hsl(var(--atlas-navy))] tabular-nums">
              {result.updated}
            </div>
          </div>
          <div className="rounded-lg border p-2">
            <div className="text-xs text-muted-foreground">Already current</div>
            <div className="font-display text-xl font-semibold text-[hsl(var(--atlas-navy))] tabular-nums">
              {result.unchanged_already_current}
            </div>
          </div>
          <div className="rounded-lg border p-2">
            <div className="text-xs text-muted-foreground">Missing from dataset</div>
            <div className="font-display text-xl font-semibold text-[hsl(var(--atlas-navy))] tabular-nums">
              {(result.missing_from_dataset || []).length}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => {
    try { return sessionStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
  });
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [reports, setReports] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cq, setCq] = useState("");
  const [selCountry, setSelCountry] = useState(null);
  const [countryJson, setCountryJson] = useState("");
  const [tier2, setTier2] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  const loadAll = async (t) => {
    setLoading(true);
    try {
      const [s, st, pu, rp, co, t2, an] = await Promise.all([
        api.adminStats(t), api.adminGetSettings(t), api.adminPurchases(t),
        api.adminReports(t), api.adminCountries(t), api.adminTier2(t), api.adminAnalytics(t),
      ]);
      setStats(s); setSettings(st); setPurchases(pu.transactions);
      setReports(rp.reports); setCountries(co.countries);
      setTier2(t2); setAnalytics(an);
      setAuthed(true);
      try { sessionStorage.setItem(TOKEN_KEY, t); } catch { /* storage unavailable — token stays in memory only */ }
    } catch (e) {
      toast.error("Invalid admin token"); setAuthed(false);
    } finally { setLoading(false); }
  };

  // Auto-attempt login on mount if a token was recovered from sessionStorage.
  // We deliberately run this only once (mountRef guard) rather than reacting to
  // `token` changes — the manual login form's Submit handler already invokes
  // loadAll(t) directly, so re-firing here would double-fetch.
  const mountRef = useRef(false);
  useEffect(() => {
    if (mountRef.current) return;
    mountRef.current = true;
    if (token) loadAll(token);
  }, [token]);

  const saveSettings = async () => {
    try {
      const r = await api.adminUpdateSettings(token, {
        weights: settings.weights, price: settings.price, pricing: settings.pricing,
      });
      setSettings(r);
      toast.success("Settings saved");
    } catch (e) { toast.error("Save failed"); }
  };
  const openCountry = async (name) => {
    try { const d = await api.adminCountry(token, name); setSelCountry(name); setCountryJson(JSON.stringify(d, null, 2)); }
    catch (e) { toast.error("Load failed"); }
  };
  const saveCountry = async () => {
    try { const payload = JSON.parse(countryJson); await api.adminUpdateCountry(token, selCountry, payload); toast.success(`${selCountry} updated`); }
    catch (e) { toast.error("Invalid JSON or save failed"); }
  };
  const setConfidential = async (id, value) => {
    try {
      await api.adminSetConfidential(token, id, value);
      toast.success(value ? "Marked confidential" : "Cleared confidential flag");
      // refresh only the reports list to avoid a full page reload
      const rp = await api.adminReports(token);
      setReports(rp.reports);
    } catch (e) { toast.error("Update failed"); }
  };
  const regenerate = async (id) => {
    try { await api.adminRegenerate(token, id); toast.success("Report regenerated"); loadAll(token); }
    catch (e) { toast.error("Regenerate failed"); }
  };

  const eff = settings?.effective_pricing;
  const pricingDraft = settings?.pricing || DEFAULT_PRICING_SHAPE;

  const updatePricing = (path, value) => {
    setSettings((s) => {
      const next = { ...s, pricing: JSON.parse(JSON.stringify(s.pricing || DEFAULT_PRICING_SHAPE)) };
      const keys = path.split(".");
      let cur = next.pricing;
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = cur[keys[i]] ?? {};
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const wtpRows = useMemo(() => {
    if (!tier2?.wtp_distribution) return [];
    return Object.entries(tier2.wtp_distribution).sort((a, b) => b[1] - a[1]);
  }, [tier2]);

  if (!authed) {
    return (
      <div className="mx-auto max-w-md px-4 py-24">
        <Card className="p-6 bg-white">
          <div className="flex items-center gap-2 text-[hsl(var(--atlas-navy))]"><ShieldAlert className="h-5 w-5" /><h1 className="font-display text-xl font-semibold">Admin access</h1></div>
          <p className="text-sm text-muted-foreground mt-2">Enter the admin token to manage the dataset, scoring weights, pricing, purchases, reports and Tier-2 interest.</p>
          <Label className="mt-4 block text-xs text-muted-foreground">Admin token</Label>
          <Input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Admin token" className="mt-1" data-testid="admin-token-input" />
          <Button className="mt-4 w-full bg-[hsl(var(--atlas-navy))] text-white" onClick={() => loadAll(token)} disabled={loading} data-testid="admin-login-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogIn className="h-4 w-4 mr-2" /> Enter</>}
          </Button>
        </Card>
      </div>
    );
  }

  const filteredCountries = countries.filter((c) => c.country.toLowerCase().includes(cq.toLowerCase()));

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-semibold text-[hsl(var(--atlas-navy))]">Admin</h1>
        <Button variant="outline" size="sm" onClick={() => loadAll(token)}><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
      </div>

      {stats && (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="admin-stats-grid">
          {[
            ["Reports", stats.total_reports, Users],
            ["Paid", stats.paid_reports, DollarSign],
            ["Base (Citizenship)", stats.base_reports, Tag],
            ["Full (Mobility)", stats.full_reports, Tag],
            ["Conversion", `${stats.conversion_pct}%`, Sliders],
            ["Revenue", `€${stats.revenue}`, DollarSign],
            ["Tier-2 interest", stats.tier2_interest ?? 0, Flame],
            [
              "Countries covered",
              `${stats.countries_with_citizenship_data ?? "—"}/${stats.total_countries ?? "—"}`,
              Globe2,
            ],
          ].map(([l, v, Icon]) => (
            <Card key={l} className="p-4 bg-white">
              <Icon className="h-4 w-4 text-[hsl(var(--atlas-steel))]" />
              <div className="mt-2 font-display text-2xl font-semibold text-[hsl(var(--atlas-navy))] tabular-nums">{v}</div>
              <div className="text-xs text-muted-foreground">{l}</div>
            </Card>
          ))}
        </div>
      )}

      <Tabs defaultValue="pricing" className="mt-8" data-testid="admin-nav">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="pricing" data-testid="admin-tab-pricing">Pricing</TabsTrigger>
          <TabsTrigger value="weights" data-testid="admin-tab-weights">Scoring weights</TabsTrigger>
          <TabsTrigger value="countries" data-testid="admin-tab-countries">Countries</TabsTrigger>
          <TabsTrigger value="purchases" data-testid="admin-tab-purchases">Purchases</TabsTrigger>
          <TabsTrigger value="reports" data-testid="admin-tab-reports">Reports</TabsTrigger>
          <TabsTrigger value="tier2" data-testid="admin-tab-tier2">Tier-2 interest</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="admin-tab-analytics">Funnel analytics</TabsTrigger>
        </TabsList>

        {/* PRICING */}
        <TabsContent value="pricing" className="mt-5">
          {settings && (
            <div className="grid gap-4 lg:grid-cols-3" data-testid="admin-pricing-editor">
              <Card className="p-6 bg-white lg:col-span-2">
                <h3 className="font-display font-semibold text-[hsl(var(--atlas-navy))]">Two-tier pricing</h3>
                <p className="text-sm text-muted-foreground">Set the standard sticker price and the promo price for each tier. When the promo below is toggled on, buyers are charged the promo price and see the standard struck through.</p>

                <div className="mt-5 grid md:grid-cols-2 gap-4">
                  {/* Citizenship tier */}
                  <div className="rounded-xl border p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Tag className="h-4 w-4 text-[hsl(var(--atlas-navy))]" />
                      <span className="font-display font-semibold text-[hsl(var(--atlas-navy))]">Citizenship Report</span>
                    </div>
                    <Label className="text-xs text-muted-foreground">Label</Label>
                    <Input value={pricingDraft.citizenship?.label ?? ""} onChange={(e) => updatePricing("citizenship.label", e.target.value)} className="mt-1" data-testid="admin-pricing-citizenship-label" />
                    <Label className="mt-3 text-xs text-muted-foreground">Blurb</Label>
                    <Textarea value={pricingDraft.citizenship?.blurb ?? ""} onChange={(e) => updatePricing("citizenship.blurb", e.target.value)} className="mt-1 text-xs h-20" />
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-muted-foreground">Standard</Label>
                        <Input type="number" step="0.01" value={pricingDraft.citizenship?.standard ?? 0} onChange={(e) => updatePricing("citizenship.standard", parseFloat(e.target.value))} className="mt-1" data-testid="admin-pricing-citizenship-standard" />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Promo price</Label>
                        <Input type="number" step="0.01" value={pricingDraft.citizenship?.promo_price ?? 0} onChange={(e) => updatePricing("citizenship.promo_price", parseFloat(e.target.value))} className="mt-1" data-testid="admin-pricing-citizenship-promo-price" />
                      </div>
                    </div>
                  </div>

                  {/* Full tier */}
                  <div className="rounded-xl border p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Tag className="h-4 w-4 text-[hsl(var(--atlas-navy))]" />
                      <span className="font-display font-semibold text-[hsl(var(--atlas-navy))]">Full Mobility Report</span>
                    </div>
                    <Label className="text-xs text-muted-foreground">Label</Label>
                    <Input value={pricingDraft.full?.label ?? ""} onChange={(e) => updatePricing("full.label", e.target.value)} className="mt-1" data-testid="admin-pricing-full-label" />
                    <Label className="mt-3 text-xs text-muted-foreground">Blurb</Label>
                    <Textarea value={pricingDraft.full?.blurb ?? ""} onChange={(e) => updatePricing("full.blurb", e.target.value)} className="mt-1 text-xs h-20" />
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-muted-foreground">Standard</Label>
                        <Input type="number" step="0.01" value={pricingDraft.full?.standard ?? 0} onChange={(e) => updatePricing("full.standard", parseFloat(e.target.value))} className="mt-1" data-testid="admin-pricing-full-standard" />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Promo price</Label>
                        <Input type="number" step="0.01" value={pricingDraft.full?.promo_price ?? 0} onChange={(e) => updatePricing("full.promo_price", parseFloat(e.target.value))} className="mt-1" data-testid="admin-pricing-full-promo-price" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 grid md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Upgrade standard</Label>
                    <Input type="number" step="0.01" value={pricingDraft.upgrade?.standard ?? 0} onChange={(e) => updatePricing("upgrade.standard", parseFloat(e.target.value))} className="mt-1" data-testid="admin-pricing-upgrade-standard" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Upgrade promo price</Label>
                    <Input type="number" step="0.01" value={pricingDraft.upgrade?.promo_price ?? 0} onChange={(e) => updatePricing("upgrade.promo_price", parseFloat(e.target.value))} className="mt-1" data-testid="admin-pricing-upgrade-promo-price" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Currency</Label>
                    <Input value={pricingDraft.currency ?? "eur"} onChange={(e) => updatePricing("currency", e.target.value)} className="mt-1" />
                  </div>
                </div>

                {/* Promo toggle. When active, every tier is charged its
                    `promo_price`; the `standard` price is struck through in
                    the UI beside a badge with `label`. */}
                <div className="mt-4 rounded-xl border border-dashed border-[hsl(var(--atlas-teal))]/50 bg-[hsl(var(--atlas-teal))]/5 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Flame className="h-4 w-4 text-[hsl(var(--atlas-teal))]" />
                      <Label className="font-medium text-[hsl(var(--atlas-navy))]">Promotional pricing</Label>
                    </div>
                    <label className="inline-flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        data-testid="admin-pricing-promo-active"
                        checked={!!pricingDraft.promo?.active}
                        onChange={(e) => updatePricing("promo.active", e.target.checked)}
                        className="h-4 w-4"
                      />
                      <span>Active</span>
                    </label>
                  </div>
                  <div className="mt-3">
                    <Label className="text-xs text-muted-foreground">Badge label</Label>
                    <Input
                      value={pricingDraft.promo?.label ?? ""}
                      onChange={(e) => updatePricing("promo.label", e.target.value)}
                      placeholder='e.g., "50% off launch"'
                      className="mt-1"
                      data-testid="admin-pricing-promo-label"
                    />
                  </div>
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    Set the exact charged prices in the tier boxes above. Stripe is charged the promo_price when this toggle is on, or the standard price when it is off.
                  </p>
                </div>

                <Button className="mt-5 bg-[hsl(var(--atlas-navy))] text-white" onClick={saveSettings} data-testid="admin-save-pricing">
                  <Save className="h-4 w-4 mr-2" /> Save pricing
                </Button>
              </Card>

              {/* Effective pricing preview */}
              <Card className="p-6 bg-white" data-testid="admin-pricing-effective">
                <h3 className="font-display font-semibold text-[hsl(var(--atlas-navy))]">Effective pricing (live)</h3>
                {eff ? (
                  <div className="mt-3 space-y-3 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      {eff.promo?.active ? (
                        <Badge className="bg-[hsl(var(--atlas-teal))] text-white border-0">
                          <Flame className="h-3 w-3 mr-1" /> {eff.promo.label || "Promo"} active
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Standard pricing</Badge>
                      )}
                    </div>
                    <div className="rounded-lg border p-3">
                      <div className="text-xs text-muted-foreground">Citizenship</div>
                      <div className="mt-1 flex items-baseline gap-2 flex-wrap">
                        <span className="font-display text-xl font-semibold text-[hsl(var(--atlas-navy))] tabular-nums">
                          {curSym(eff.currency)}{eff.citizenship.price}
                        </span>
                        {eff.promo?.active && eff.citizenship.pre_promo_price !== eff.citizenship.price && (
                          <span className="text-sm text-muted-foreground line-through tabular-nums">
                            {curSym(eff.currency)}{eff.citizenship.pre_promo_price}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="rounded-lg border p-3">
                      <div className="text-xs text-muted-foreground">Full Mobility</div>
                      <div className="mt-1 flex items-baseline gap-2 flex-wrap">
                        <span className="font-display text-xl font-semibold text-[hsl(var(--atlas-navy))] tabular-nums">
                          {curSym(eff.currency)}{eff.full.price}
                        </span>
                        {eff.promo?.active && eff.full.pre_promo_price !== eff.full.price && (
                          <span className="text-sm text-muted-foreground line-through tabular-nums">
                            {curSym(eff.currency)}{eff.full.pre_promo_price}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="rounded-lg border p-3">
                      <div className="text-xs text-muted-foreground">Upgrade (base → full)</div>
                      <div className="mt-1 flex items-baseline gap-2 flex-wrap">
                        <span className="font-display text-xl font-semibold text-[hsl(var(--atlas-navy))] tabular-nums">
                          {curSym(eff.currency)}{eff.upgrade.amount}
                        </span>
                        {eff.promo?.active && eff.upgrade.pre_promo_amount !== eff.upgrade.amount && (
                          <span className="text-sm text-muted-foreground line-through tabular-nums">
                            {curSym(eff.currency)}{eff.upgrade.pre_promo_amount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground mt-2">Loading…</p>
                )}
              </Card>
            </div>
          )}
        </TabsContent>

        {/* WEIGHTS */}
        <TabsContent value="weights" className="mt-5">
          {settings && (
            <Card className="p-6 bg-white max-w-2xl" data-testid="admin-weights-editor">
              <h3 className="font-display font-semibold text-[hsl(var(--atlas-navy))]">Accessibility scoring weights</h3>
              <p className="text-sm text-muted-foreground">Adjust how pathways are ranked. Applied to newly generated &amp; regenerated reports.</p>
              <div className="mt-4 space-y-5">
                {WEIGHT_KEYS.map((k) => (
                  <div key={k}>
                    <div className="flex justify-between text-sm">
                      <span className="capitalize text-[hsl(var(--atlas-navy))]">{k.replace("_", " ")}</span>
                      <span className="tabular-nums text-muted-foreground">{(settings.weights[k] ?? 0).toFixed(2)}</span>
                    </div>
                    <Slider value={[(settings.weights[k] ?? 0) * 100]} min={0} max={100} step={5}
                      onValueChange={(v) => setSettings((s) => ({ ...s, weights: { ...s.weights, [k]: v[0] / 100 } }))} className="mt-2" data-testid={`admin-weight-${k}`} />
                  </div>
                ))}
              </div>
              <Button className="mt-5 bg-[hsl(var(--atlas-navy))] text-white" onClick={saveSettings} data-testid="admin-save-settings">
                <Save className="h-4 w-4 mr-2" /> Save weights
              </Button>
            </Card>
          )}
        </TabsContent>

        {/* COUNTRIES */}
        <TabsContent value="countries" className="mt-5 space-y-4">
          {/* Post-deploy utility: seed lifestyle + mobility onto the ~50
              covered countries in a fresh Mongo. Idempotent — safe to click
              more than once, no-ops when data is already current. */}
          <LifestyleSeedCard token={token} />
          <div className="grid lg:grid-cols-3 gap-4" data-testid="admin-country-editor">
            <Card className="p-4 bg-white lg:col-span-1">
              <Input value={cq} onChange={(e) => setCq(e.target.value)} placeholder="Search countries…" className="mb-3" />
              <div className="max-h-[60vh] overflow-auto thin-scroll space-y-1">
                {filteredCountries.map((c) => (
                  <button key={c.country} onClick={() => openCountry(c.country)} className={`w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-[hsl(var(--accent))] flex items-center justify-between ${selCountry === c.country ? "bg-[hsl(var(--accent))]" : ""}`}>
                    {c.country}{c.has_citizenship_data && <Badge variant="secondary" className="text-[10px]">cit</Badge>}
                  </button>
                ))}
              </div>
            </Card>
            <Card className="p-4 bg-white lg:col-span-2">
              {selCountry ? (<>
                <div className="flex items-center justify-between">
                  <h3 className="font-display font-semibold text-[hsl(var(--atlas-navy))]">{selCountry}</h3>
                  <Button size="sm" onClick={saveCountry} className="bg-[hsl(var(--atlas-navy))] text-white" data-testid="admin-save-country">
                    <Save className="h-4 w-4 mr-1" /> Save
                  </Button>
                </div>
                <Textarea value={countryJson} onChange={(e) => setCountryJson(e.target.value)} className="mt-3 font-mono text-xs h-[55vh]" data-testid="admin-country-json" />
              </>) : <div className="text-center text-muted-foreground py-20">Select a country to edit its record.</div>}
            </Card>
          </div>
        </TabsContent>

        {/* PURCHASES */}
        <TabsContent value="purchases" className="mt-5">
          <Card className="bg-white overflow-hidden">
            <div className="overflow-auto thin-scroll">
              <table className="w-full text-sm text-left">
                <thead className="bg-[hsl(var(--muted))] text-xs text-[hsl(var(--atlas-slate))]">
                  <tr>{["Date", "Email", "Tier", "Kind", "Amount", "Status", "Report"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {purchases.map((p, i) => (
                    // Prefer the Stripe session_id (globally unique) so React can
                    // reconcile rows correctly when the list is refreshed / re-sorted.
                    // Fall back to index only if session_id is missing on a legacy row.
                    <tr key={p.session_id || `row-${i}`} className="border-t">
                      <td className="px-3 py-2 text-muted-foreground">{(p.created_at || "").slice(0, 10)}</td>
                      <td className="px-3 py-2">{p.email}</td>
                      <td className="px-3 py-2 capitalize">{p.tier || "—"}</td>
                      <td className="px-3 py-2 capitalize">{p.kind || "—"}</td>
                      <td className="px-3 py-2 tabular-nums">{curSym(p.currency)}{p.amount}</td>
                      <td className="px-3 py-2">
                        <Badge className={p.payment_status === "paid" ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-amber-50 text-amber-900 border border-amber-200"}>
                          {p.payment_status}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{(p.report_id || "").slice(0, 8)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {purchases.length === 0 && <div className="py-10 text-center text-muted-foreground">No purchases yet.</div>}
            </div>
          </Card>
        </TabsContent>

        {/* REPORTS */}
        <TabsContent value="reports" className="mt-5">
          <Card className="bg-white overflow-hidden" data-testid="admin-reports-table">
            <div className="overflow-auto thin-scroll">
              <table className="w-full text-sm text-left">
                <thead className="bg-[hsl(var(--muted))] text-xs text-[hsl(var(--atlas-slate))]">
                  <tr>{["Date", "Name", "Email", "Paid", "Tier", "Pathways", "Confidential", ""].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr>
                </thead>
                <tbody>{reports.map((r) => (
                  <tr key={r.id} className="border-t">
                    <td className="px-3 py-2 text-muted-foreground">{(r.generated_at || "").slice(0, 10)}</td>
                    <td className="px-3 py-2">{r.profile?.name || "—"}</td>
                    <td className="px-3 py-2">{r.email || "—"}</td>
                    <td className="px-3 py-2">{r.paid ? <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200">paid</Badge> : <Badge variant="secondary">locked</Badge>}</td>
                    <td className="px-3 py-2 capitalize">{r.tier_purchased || "—"}</td>
                    <td className="px-3 py-2 tabular-nums">{r.teaser?.total_pathways}</td>
                    <td className="px-3 py-2">
                      <Switch
                        checked={!!r.confidential}
                        onCheckedChange={(v) => setConfidential(r.id, v)}
                        data-testid={`admin-confidential-${r.id.slice(0, 8)}`}
                        aria-label={`Toggle confidential for report ${r.id.slice(0, 8)}`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="outline" data-testid={`admin-regen-${r.id.slice(0, 8)}`}>
                            <RefreshCw className="h-3.5 w-3.5" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Regenerate report?</AlertDialogTitle>
                            <AlertDialogDescription>This re-runs the engine with current data &amp; scoring weights for this report.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => regenerate(r.id)}>Regenerate</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
              {reports.length === 0 && <div className="py-10 text-center text-muted-foreground">No reports yet.</div>}
            </div>
          </Card>
        </TabsContent>

        {/* TIER 2 */}
        <TabsContent value="tier2" className="mt-5">
          <div className="grid lg:grid-cols-3 gap-4" data-testid="admin-tier2-view">
            <Card className="p-5 bg-white lg:col-span-1">
              <div className="flex items-center gap-2">
                <HeartHandshake className="h-4 w-4 text-[hsl(var(--atlas-navy))]" />
                <h3 className="font-display font-semibold text-[hsl(var(--atlas-navy))]">Interest overview</h3>
              </div>
              <div className="mt-3 space-y-2">
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground">Total registrations</div>
                  <div className="font-display text-2xl font-semibold text-[hsl(var(--atlas-navy))] tabular-nums">
                    {tier2?.count ?? 0}
                  </div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground">Came from CBI context</div>
                  <div className="font-display text-2xl font-semibold text-[hsl(var(--atlas-navy))] tabular-nums">
                    {tier2?.cbi_context_count ?? 0}
                  </div>
                </div>
                {wtpRows.length > 0 && (
                  <div className="rounded-lg border p-3">
                    <div className="text-xs text-muted-foreground mb-2">WTP distribution</div>
                    <div className="space-y-1 text-sm">
                      {wtpRows.map(([band, n]) => (
                        <div key={band} className="flex items-center justify-between">
                          <span className="text-[hsl(var(--atlas-slate))]">{band}</span>
                          <Badge variant="outline" className="tabular-nums">{n}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Card>

            <Card className="bg-white overflow-hidden lg:col-span-2">
              <div className="overflow-auto thin-scroll">
                <table className="w-full text-sm text-left">
                  <thead className="bg-[hsl(var(--muted))] text-xs text-[hsl(var(--atlas-slate))]">
                    <tr>{["Date", "Email", "WTP band", "CBI ctx", "Report"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {(tier2?.interest || []).map((t) => (
                      <tr key={t.id} className="border-t">
                        <td className="px-3 py-2 text-muted-foreground">{(t.created_at || "").slice(0, 10)}</td>
                        <td className="px-3 py-2">{t.email}</td>
                        <td className="px-3 py-2">{t.wtp_band || "—"}</td>
                        <td className="px-3 py-2">{t.cbi_context ? <Badge className="bg-[hsl(var(--accent))] text-[hsl(var(--atlas-navy))] border-0">yes</Badge> : "—"}</td>
                        <td className="px-3 py-2 font-mono text-xs">{(t.report_id || "").slice(0, 8)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(!tier2?.interest || tier2.interest.length === 0) && <div className="py-10 text-center text-muted-foreground">No Tier-2 interest yet.</div>}
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* ANALYTICS */}
        <TabsContent value="analytics" className="mt-5 space-y-4">
          {/* Conversion funnel — the marquee visualization for this tab */}
          <Card className="p-6 bg-white" data-testid="admin-funnel-conversion-view">
            <div className="flex items-center gap-2">
              <LineChart className="h-4 w-4 text-[hsl(var(--atlas-navy))]" />
              <h3 className="font-display font-semibold text-[hsl(var(--atlas-navy))]">Conversion funnel</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              Step-to-step conversion through the core acquisition funnel. Each bar is scaled to
              the top-of-funnel (intake started); percentages are step-to-step drop-off.
            </p>

            {analytics?.entry_sources && (
              <div className="mt-4 grid grid-cols-2 gap-3 max-w-md" data-testid="admin-funnel-entry-sources">
                <div className="rounded-lg border p-3 bg-[hsl(var(--muted))]/30">
                  <div className="text-xs text-muted-foreground">Tax-Explorer bridge clicks</div>
                  <div className="mt-1 font-display text-2xl font-semibold text-[hsl(var(--atlas-navy))] tabular-nums">
                    {analytics.entry_sources.tax_explorer_bridge}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    CTA banner + row clicks combined
                  </div>
                </div>
                <div className="rounded-lg border p-3 bg-[hsl(var(--muted))]/30">
                  <div className="text-xs text-muted-foreground">Intake started (funnel top)</div>
                  <div className="mt-1 font-display text-2xl font-semibold text-[hsl(var(--atlas-navy))] tabular-nums">
                    {analytics.entry_sources.intake_started}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    100% baseline for conversion rates below
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 space-y-2" data-testid="admin-funnel-conversion-bars">
              {(analytics?.conversion || []).map((row) => {
                const cumClamped = row.cumulative_rate != null ? Math.min(row.cumulative_rate, 1) : 0;
                const width = row.cumulative_rate != null ? Math.max(cumClamped * 100, 2) : 0;
                const stepRaw = row.step_rate;
                const overflow = stepRaw != null && stepRaw > 1.05; // >105% means the stage has more events than the previous
                const stepPct = stepRaw != null ? Math.round(stepRaw * 100) : null;
                const cumPct = row.cumulative_rate != null ? Math.round(row.cumulative_rate * 100) : null;
                const stepColor = stepPct == null ? "text-muted-foreground"
                  : overflow ? "text-muted-foreground"
                  : stepPct >= 60 ? "text-emerald-700"
                  : stepPct >= 30 ? "text-amber-700"
                  : "text-rose-700";
                const barColor = stepPct == null ? "hsl(var(--atlas-navy))"
                  : overflow ? "hsl(var(--atlas-steel))"
                  : stepPct >= 60 ? "hsl(158 55% 40%)"
                  : stepPct >= 30 ? "hsl(38 90% 50%)"
                  : "hsl(0 65% 55%)";
                return (
                  <div
                    key={row.stage}
                    data-testid={`admin-funnel-row-${row.stage}`}
                    className="rounded-lg border bg-white p-3"
                  >
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                      <div className="flex items-baseline gap-2 min-w-0">
                        <span className="text-sm font-medium text-[hsl(var(--atlas-navy))] capitalize">
                          {row.stage.replace(/_/g, " ")}
                        </span>
                        <span className="text-xs text-muted-foreground tabular-nums">
                          {row.count} {row.count === 1 ? "event" : "events"}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs tabular-nums">
                        {overflow ? (
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-muted-foreground italic cursor-help">
                                  n/a
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="left" className="max-w-xs text-xs">
                                This stage has more events than the previous one ({stepPct}%),
                                usually because events accumulated before earlier stages were
                                instrumented. Rates will normalize as new-instrumentation-era
                                traffic dominates.
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : stepPct != null ? (
                          <span className={`font-medium ${stepColor}`}>
                            {stepPct}% from prev
                          </span>
                        ) : null}
                        {cumPct != null && (
                          <span className="text-muted-foreground">
                            {overflow ? `${cumPct}% of top` : `${Math.min(cumPct, 100)}% of top`}
                          </span>
                        )}
                        {row.drop_off != null && row.drop_off > 0 && (
                          <span className="text-rose-700/80">
                            −{row.drop_off} lost
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 h-2 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${width}%`, backgroundColor: barColor }}
                      />
                    </div>
                  </div>
                );
              })}
              {(!analytics?.conversion || analytics.conversion.length === 0) && (
                <div className="py-10 text-center text-muted-foreground">
                  No conversion data yet. The funnel needs at least one intake_started event.
                </div>
              )}
            </div>

            {analytics?.conversion && analytics.conversion[0]?.count === 0 && (
              <p className="mt-4 text-xs text-amber-800 bg-amber-50/70 border border-amber-200 rounded-lg px-3 py-2">
                <span className="font-medium">Funnel is empty:</span> no `intake_started` events yet.
                Percentages become available as users complete the wizard.
              </p>
            )}
          </Card>

          {/* All events (raw counts) — kept for parity with the previous view */}
          <Card className="p-6 bg-white" data-testid="admin-analytics-view">
            <div className="flex items-center gap-2">
              <LineChart className="h-4 w-4 text-[hsl(var(--atlas-navy))]" />
              <h3 className="font-display font-semibold text-[hsl(var(--atlas-navy))]">All events</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              Raw counts by event, ordered by canonical funnel where possible. Non-funnel events
              (e.g. `report_viewed`) appear at the end.
            </p>
            <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {(analytics?.funnel || []).map((f) => (
                <div key={f.event} className="rounded-lg border p-3 bg-[hsl(var(--muted))]/30">
                  <div className="text-xs text-muted-foreground">{f.event.replace(/_/g, " ")}</div>
                  <div className="mt-1 font-display text-2xl font-semibold text-[hsl(var(--atlas-navy))] tabular-nums">{f.count}</div>
                </div>
              ))}
              {(!analytics?.funnel || analytics.funnel.length === 0) && (
                <div className="col-span-full py-10 text-center text-muted-foreground">No analytics events yet.</div>
              )}
            </div>
            {analytics && (
              <div className="mt-4 text-xs text-muted-foreground">
                Total events tracked: <span className="tabular-nums">{analytics.total_events}</span>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
